import type { Movimentacao, Solicitacao } from '../../models';
import { ehItemSimples, mesclarEquipamentosMovimentacao } from '../../models';
import { TIPOS_EQUIPAMENTO_COM_SENHA, existeAcessoPendente } from './solicitacaoDetailsPage.definitions';

export interface Pendencia {
  emoji: string;
  label: string;
}

/**
 * Portada da fórmula em cascata da planilha antiga do SI (1ª condição não atendida
 * "vence" e vira o próximo passo a fazer) — objetivo é a mesma verificação prática e
 * rápida do que falta pra uma Movimentação/Solicitação poder ser concluída, sem precisar
 * abrir cada seção pra descobrir. O "tipo de equipamento" vem antes de tudo porque,
 * diferente da planilha (sempre preenchido), neste app ele pode ficar em aberto pro SI
 * completar depois (ex. desligamento) — sem ele, nenhuma das checagens seguintes faz
 * sentido. Patrimônio/Nº Série vêm logo em seguida (identificação do equipamento).
 *
 * Dali em diante a ordem muda por `tipoFluxo`, porque só faz sentido cobrar Condição/
 * Acessórios/Fotos/Termo quando o equipamento já está fisicamente com quem vai preencher
 * isso: no envio a Raro já está com ele antes mesmo de postar (então vem antes da
 * logística — Senha/Código do Objeto/Datas); na devolução só depois que a Raro recebe de
 * volta (então vem depois da logística). GLPI fica por último nos dois fluxos.
 *
 * "Informativos" (Proteja seu Equipamento / BMG) ficaram de fora de propósito — o campo
 * existe no modelo (`MovimentacaoEtapas.informativos`) mas nada resolve/preenche isso
 * ainda (nenhuma regra de quando disparar, nenhuma UI) — feature própria, futura.
 */
export function pendenciaMovimentacao(movimentacao: Movimentacao): Pendencia | null {
  if (movimentacao.statusMovimentacao === 'cancelada') return null;

  const itens = mesclarEquipamentosMovimentacao(
    movimentacao.equipamentosIntake ?? [],
    movimentacao.equipamentosExecucao ?? []
  );

  for (const item of itens) {
    if (item.tipoEquipamento === null) return { emoji: '❔', label: 'Definir Tipo de Equipamento' };
  }
  for (const item of itens) {
    if (item.tipoEquipamento !== 'periferico' && !item.patrimonio) {
      return { emoji: '🏷️', label: 'Informar Patrimônio' };
    }
  }
  for (const item of itens) {
    // Cartão de acesso e chip: o Inventário não exige nº de série.
    if (item.tipoEquipamento !== 'periferico' && !ehItemSimples(item.tipoEquipamento) && !item.nSerie) {
      return { emoji: '🔢', label: 'Informar Nº Série' };
    }
  }

  const pendenciaCondicao = (): Pendencia | null => {
    for (const item of itens) {
      if (!item.condicao) return { emoji: '🔧', label: 'Informar Condição do Equipamento' };
    }
    for (const item of itens) {
      const temAcessorioInformado = (item.acessorios && item.acessorios.length > 0) || Boolean(item.acessoriosOutros);
      if (!temAcessorioInformado) return { emoji: '🎒', label: 'Informar Acessórios' };
    }
    return null;
  };

  const pendenciaFotos = (): Pendencia | null =>
    movimentacao.etapas.fotos !== 'concluido' ? { emoji: '📷', label: 'Anexar Fotos' } : null;
  // O termo leva a data da logística (recebimento no envio, envio na devolução) — por isso
  // vem sempre DEPOIS dela nas duas pontas.
  const pendenciaTermo = (): Pendencia | null => {
    if (movimentacao.etapas.termo !== 'concluido') {
      // Termo já gerado, falta o SI enviar pra assinatura (e marcar "Termo Enviado").
      return movimentacao.termoUrl
        ? { emoji: '✍️', label: 'Enviar Termo para Assinatura' }
        : { emoji: '📝', label: 'Criar Termo' };
    }
    return null;
  };

  if (movimentacao.tipoFluxo === 'envio') {
    const pendenciaEquipamento = pendenciaCondicao();
    if (pendenciaEquipamento) return pendenciaEquipamento;

    for (const item of itens) {
      if (
        item.tipoEquipamento &&
        TIPOS_EQUIPAMENTO_COM_SENHA.includes(item.tipoEquipamento) &&
        !item.homologado &&
        !item.senha
      ) {
        return { emoji: '🔑', label: 'Preencher Senha' };
      }
    }

    const pendenciaDeFotos = pendenciaFotos();
    if (pendenciaDeFotos) return pendenciaDeFotos;

    if (movimentacao.metodoEntrega === 'correios' && !movimentacao.codigoRastreio) {
      return { emoji: '📦', label: 'Informar Código do Objeto' };
    }
    if (!movimentacao.dataEnvio) return { emoji: '📤', label: 'Informar Data de Envio' };
    if (!movimentacao.dataRecebimento) return { emoji: '📥', label: 'Informar Data de Recebimento' };

    const pendenciaDoTermo = pendenciaTermo();
    if (pendenciaDoTermo) return pendenciaDoTermo;
  } else {
    if (movimentacao.metodoEntrega === 'correios' && !movimentacao.codigoRastreio) {
      return { emoji: '📦', label: 'Informar Código do Objeto' };
    }
    if (!movimentacao.dataEnvio) return { emoji: '📤', label: 'Informar Data de Envio' };
    if (!movimentacao.dataRecebimento) return { emoji: '📥', label: 'Informar Data de Recebimento' };

    const pendenciaEquipamento = pendenciaCondicao();
    if (pendenciaEquipamento) return pendenciaEquipamento;

    const pendenciaDocumentacao = pendenciaFotos() ?? pendenciaTermo();
    if (pendenciaDocumentacao) return pendenciaDocumentacao;
  }

  // Último passo das duas pontas (espelha concluirSolicitacao): termo assinado anexado.
  if (!movimentacao.termoAssinado?.url) return { emoji: '📎', label: 'Anexar Termo Assinado' };

  for (const item of itens) {
    if (!item.glpiAtualizado) {
      return {
        emoji: '💻',
        label: movimentacao.tipoFluxo === 'envio' ? 'Vincular Ativo no GLPI' : 'Desvincular Ativo no GLPI',
      };
    }
  }
  return null;
}

/**
 * Pendência "geral" da Solicitação pra quem só quer bater o olho. Ordem de prioridade
 * (mesma no client e em functions/src/si/concluirSolicitacao.ts):
 *
 * 1. Vincular Colaborador (só admissão, enquanto `colaboradorId` ainda é null) — sem
 *    saber quem é a pessoa, nenhuma das checagens seguintes faz sentido de verdade.
 * 2. Admissão/Acessos: Acessos primeiro (decisão de produto), como já era.
 *    Desligamento: Acessos fica por ÚLTIMO — combinado que, na prática, o corte de
 *    acesso só acontece no último dia trabalhado, enquanto os dados do equipamento
 *    (Movimentação) são coletados bem antes disso, então cobrar Acessos cedo só geraria
 *    alerta "pendente" o processo inteiro sem ação possível ainda.
 * 3. Movimentação (primeira com pendência).
 *
 * `null` = tudo OK, pode concluir.
 */
export function pendenciaSolicitacao(
  solicitacao: Pick<Solicitacao, 'acessos' | 'tipoSolicitacao' | 'colaboradorId'>,
  movimentacoes: Movimentacao[]
): Pendencia | null {
  if (solicitacao.tipoSolicitacao === 'admissao' && solicitacao.colaboradorId == null) {
    return { emoji: '🔗', label: 'Vincular Colaborador' };
  }

  // Admissão só cria acesso, Desligamento só remove — a ação já é implícita pelo tipo
  // (mesmo raciocínio de `acao` em AcessosChecklist), então o rótulo pode ser específico
  // em vez do genérico "Criar/Remover" (que só faz sentido pro tipo 'acessos' pontual,
  // onde a ação varia por item — ver acaoLabel em AcessosChecklist).
  const pendenciaAcessos = (): Pendencia | null => {
    if (!existeAcessoPendente(solicitacao.tipoSolicitacao, solicitacao.acessos ?? [])) return null;
    const label =
      solicitacao.tipoSolicitacao === 'admissao'
        ? 'Criar Acessos'
        : solicitacao.tipoSolicitacao === 'desligamento'
          ? 'Remover Acessos'
          : 'Criar/Remover Acessos';
    return { emoji: '👤', label };
  };

  if (solicitacao.tipoSolicitacao !== 'desligamento') {
    const pendencia = pendenciaAcessos();
    if (pendencia) return pendencia;
  }

  for (const movimentacao of movimentacoes) {
    const pendencia = pendenciaMovimentacao(movimentacao);
    if (pendencia) return pendencia;
  }

  if (solicitacao.tipoSolicitacao === 'desligamento') {
    const pendencia = pendenciaAcessos();
    if (pendencia) return pendencia;
  }

  return null;
}
